/*************************************************

* BAKER HUGHES COMPANY CONFIDENTIAL

* Unpublished work copyright 2025 Baker Hughes Company.  All rights reserved.

*

* This computer code is proprietary and highly confidential to Baker Hughes Company

* and/or its affiliate(s). It may not be used, disclosed, modified, transferred,

* or reproduced without prior written consent, and must be returned on demand.

**************************************************/
import { useEntity } from '@backstage/plugin-catalog-react';
import React, { useEffect, useState } from 'react';
import { fetchEndOfLifeVersion } from './toolsHelper';
import { Table, TableColumn } from '@backstage/core-components';

interface EndOfLifeData {
  cycle: string;
  eol: boolean;
  latest: string;
  latestReleaseDate: string;
  lts: boolean;
  releaseDate: string;
}

interface TagData {
  tag_name: string;
  date: string;
}

type ToolData = EndOfLifeData | TagData;

const ToolsVersionSupport: React.FC = () => {
  const [tools, setTools] = useState<ToolData[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const { entity } = useEntity();

  useEffect(() => {
    const fetchTools = async () => {
      try {
        const response: ToolData[] | null = await fetchEndOfLifeVersion(
          entity?.metadata?.name,
        );
      
        setTools(response || []);
      } catch (err: any) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchTools();
  }, [entity]);

  if (loading) {
    return <div>Loading...</div>;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = date.toLocaleString('default', { month: 'short' });
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  };

  const getRelativeTime = (dateString: string): string => {
    const now = new Date();
    const date = new Date(dateString);
    const diffInMs = now.getTime() - date.getTime();
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

    if (diffInDays < 30) {
      return `${diffInDays} days ago`;
    } else if (diffInDays < 365) {
      const months = Math.floor(diffInDays / 30);
      return `${months} months ago`;
    } else {
      const years = Math.floor(diffInDays / 365);
      return `${years} years ago`;
    }
  };

  const columns: TableColumn[] = [
    {
      title: 'Release',
      field: 'release',
      render: (row: any) => row.tag_name || row.cycle || '-',
    },
    {
      title: 'Released',
      field: 'released',
      render: (row: any) =>
        row.date
          ? `${getRelativeTime(row.date)} (${formatDate(row.date)})`
          : row.releaseDate
          ? `${getRelativeTime(row.releaseDate)} (${formatDate(
              row.releaseDate,
            )})`
          : '-',
    },
    {
      title: 'Support',
      field: 'support',
      render: (row: any) => {

        if (row.eol) {
          return (
            <span style={{ color: 'red', fontWeight: 'bold' }}>
              NO - Ended {getRelativeTime(row.eol)} ({formatDate(row.eol)})
            </span>
          );
        }
        if (!row.eol) {
          return <span style={{ color: 'red', fontWeight: 'bold' }}>-</span>;
        }
        return <span style={{ color: 'green', fontWeight: 'bold' }}>YES</span>;
      },
    },
    {
      title: 'Latest',
      field: 'latest',
      render: (row: any) =>
        row.index === 0
          ? row.tag_name
            ? `${row.tag_name} (${formatDate(row.date || '')})`
            : row.latest
            ? `${row.latest} (${formatDate(row.latestReleaseDate || '')})`
            : '-'
          : '',
    },
  ];

  const data = tools.map((tool, index) => ({
    ...tool,
    index, // Add index to the row data for conditional rendering
  }));

  return (
    <div>
      <Table
        title="Tools Version Support"
        options={{ search: false, paging: false }}
        columns={columns}
        data={data}
      />
      
    </div>
  );
};

export default ToolsVersionSupport;
